import "./GDrawings.scss";
import {
    ArrowRight,
    Code2,
    Globe,
    Laptop,
    Lightbulb,
    Rocket,
    Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {useTranslation} from "react-i18next";
import {DiscordMember, GalleryItem} from "../../types";
import Card from "../../Components/CardGallery/CardGallery";
import {discordUrl} from "../../Global";
import {useState} from "react";

const steps = [
    {
        id: "01",
        icon: Lightbulb,
    },
    {
        id: "02",
        icon: Laptop,
    },
    {
        id: "03",
        icon: Code2,
    },
    {
        id: "04",
        icon: Rocket,
    },
];

interface GDrawingProps {
    staff: DiscordMember[] | [];
    workGraphic: GalleryItem[];
}


export default function GDrawings({ staff, workGraphic }: GDrawingProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [maxPhoto, setMaxPhoto] = useState<number>(5);

    const graphicsDrawings = staff.filter((member) =>
        member.roles.some((role) => role.id === '1558566850974060654')
    );

    return (
        <section className="graphics-drawings">
            {/* ===== HERO ===== */}

            <div className="graphics-drawings__hero">
                <div className="graphics-drawings__hero-container">
                    {/* Left */}
                    <div className="graphics-drawings__hero-content">
                        <h1 className="graphics-drawings__title">
                            {t("graphic-design.title.up")}
                            <br />
                            {t("graphic-design.title.down")}<span>{t("graphic-design.title.bold")}</span>
                        </h1>
                        <p className="graphics-drawings__description">
                            {t("graphic-design.subtitle")}
                        </p>
                        <div className="graphics-drawings__actions">
                            <a href={discordUrl} target="_blank" className="graphics-drawings__button" rel="noreferrer">
                                {t("graphic-design.preventive")}
                                <ArrowRight size={18} />
                            </a>
                            {workGraphic.length > 0 && (
                                <a href="#portfolio" className="graphics-drawings__button graphics-drawings__button--secondary">
                                    {t("graphic-design.lookWorks")}
                                </a>
                            )}
                        </div>
                    </div>

                    {/*Presentation*/}
                </div>
            </div>

            {/* ===== STEP ===== */}
            <div className="graphics-drawings__steps">
                <div className="graphics-drawings__steps-container">
                    <header className="graphics-drawings__steps-header">
                        <h2>{t(`graphic-design.develop.title`)}</h2>
                        <p>{t(`graphic-design.develop.subtitle`)}</p>
                    </header>

                    <div className="graphics-drawings__steps-grid">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <div className="graphics-drawings__step" key={step.id}>
                                    <div className="graphics-drawings__step-icon">
                                        <Icon size={24} />
                                    </div>
                                    <h3 className="graphics-drawings__step-title">{t(`graphic-design.develop.card.${step.id}.title`)}</h3>
                                    <p className="graphics-drawings__step-description">{t(`graphic-design.develop.card.${step.id}.subtitle`)}</p>
                                    {index < steps.length - 1 && (
                                        <div className="graphics-drawings__step-connector">
                                            <Zap size={14} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== TEAM ===== */}
            {graphicsDrawings.length > 0 && (
                <div className="graphics-drawings__team">
                <div className="graphics-drawings__team-container">
                    <header className="graphics-drawings__team-header">
                        <h2>{t(`graphic-design.employees.title`)}</h2>
                        <p>{t(`graphic-design.employees.subtitle`)}</p>
                    </header>

                    <div className="graphics-drawings__team-grid">
                        {graphicsDrawings.map((dev) => (
                            <a
                                key={dev.id}
                                target="_blank"
                                href={dev.avatar}
                                rel="noopener noreferrer"
                                className="graphics-drawings__team-card"
                            >
                                <div className="graphics-drawings__team-avatar">
                                    <img src={dev.avatar} alt={dev.username} />
                                    <div className={`graphics-drawings__team-status graphics-drawings__team-status_${dev.online}`} />
                                </div>
                                <h3>{dev.username}</h3>
                                <span>Graphic Designer</span>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
            )}

            {graphicsDrawings.length > 0 && (
                <div className="graphics-drawings__portfolio" id="portfolio">
                <div className="graphics-drawings__portfolio-container">
                    <header className="graphics-drawings__portfolio-header">
                        <h2>{t(`graphic-design.works.title`)}</h2>
                        <p>{t(`graphic-design.works.subtitle`)}</p>
                    </header>

                    <div className="graphics-drawings__gallery__container">
                        <div className="gallery">
                        {workGraphic.slice(0, maxPhoto).map((photos) => (
                            <Card
                                item={photos}
                            />
                        ))}
                            {workGraphic.length > maxPhoto && (
                            <div onClick={() => setMaxPhoto(workGraphic.length)} className="card gallery-more-card">
                                <div className="gallery-more-card__content">
                                    <p>{t("works.graphic.other")}</p>
                                    <h1>+{workGraphic.length - maxPhoto}</h1>
                                </div>
                            </div>
                            )}
                        </div>

                    </div>
                </div>

            </div>
            )}
        </section>
    );
}
